// Frisläpp plockade ordrar (mockup shell-v1/menu-areas.html §2, owner 2026-11-01): when the company
// runs pick and release as two steps, the picked orders wait here until someone releases them —
// one at a time, or every order a carrier takes when the truck leaves. The release is the ERP's
// finish step: stock leaves, invoicing may follow.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Api, type SalesOrderDetail, type SalesOrderSummary } from "../api/client.js";
import { t } from "../i18n.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, ListItem, Prompt, Rows, ScanBar, Strip, focusScan, type BannerData } from "../ui/components.jsx";

const hhmm = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) : "");
type Picking = { pallets: { sscc: string; items: string[] }[]; lines: { lineNo: number; lots: string[]; quantity: string; short: string | null }[] };

export function Release({ onClose }: { onClose: () => void }) {
  const [orders, setOrders] = useState<SalesOrderSummary[] | null>(null);
  const [details, setDetails] = useState<Map<string, SalesOrderDetail>>(new Map());
  const [chosen, setChosen] = useState<SalesOrderDetail | null>(null);
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    Api.salesOrders("picked").then(async (o) => {
      const open = o.filter((x) => !x.releasedAt).sort((a, b) => (a.pickupAt ?? "9").localeCompare(b.pickupAt ?? "9") || (a.pickedAt ?? "").localeCompare(b.pickedAt ?? ""));
      setOrders(open);
      // The details (pallets' SSCCs, lines with lots) so a pallet scan finds its order.
      const ds = await Promise.all(open.map((x) => Api.salesOrder(x.id).catch(() => null)));
      setDetails(new Map(ds.filter((d): d is SalesOrderDetail => !!d).map((d) => [d.id, d])));
    }).catch(() => setOrders([]));
  }, []);
  useEffect(load, [load]);

  const byCarrier = useMemo(() => {
    const m = new Map<string, SalesOrderSummary[]>();
    for (const o of orders ?? []) if (o.carrier) m.set(o.carrier, [...(m.get(o.carrier) ?? []), o]);
    return [...m.entries()].filter(([, xs]) => xs.length > 1);
  }, [orders]);

  const open = (o: SalesOrderSummary) => { const d = details.get(o.id); if (d) { feedback("accepted"); setChosen(d); } };
  const onScan = useCallback((raw: string) => {
    const text = raw.replace(/^\]\w\w/, "").trim();
    const sscc = /^\(00\)(\d{18})$/.exec(text)?.[1] ?? /^00(\d{18})$/.exec(text)?.[1] ?? (/^\d{18}$/.test(text) ? text : null);
    const hit = (orders ?? []).find((o) => o.reference === text || (sscc && (details.get(o.id)?.picking as Picking | null)?.pallets.some((p) => p.sscc === sscc)));
    if (hit) open(hit);
    else { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("release.notFound", { code: text.slice(0, 40) }) }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, details]);
  useEffect(() => {
    const el = inputRef.current;
    if (!el || chosen) return;
    focusScan(el);
    return attachWedge(el, { onScan });
  }, [onScan, chosen]);

  const release = async (ids: string[]) => {
    setBusy(true);
    let ok = 0;
    for (const id of ids) { try { await Api.releaseOrder(id); ok++; } catch { /* stays in the list */ } }
    setBusy(false);
    feedback(ok === ids.length ? "accepted" : "rejected");
    setBanner(ok === ids.length ? { kind: "ok", label: t("release.done"), message: t("release.doneMsg", { n: ok }) } : { kind: "warn", label: t("release.partly"), message: t("release.partlyMsg", { ok, n: ids.length }) });
    setChosen(null);
    load();
  };

  if (chosen) {
    const picking = chosen.picking as Picking | null;
    const rows = chosen.lineItems.map((l) => { const p = picking?.lines.find((x) => x.lineNo === l.lineNo); return { k: `${t("release.line", { n: l.lineNo })} · ${l.productName ?? l.description ?? l.sku ?? ""}`, small: p?.lots.join(", ") ?? "", q: `${p?.quantity ?? "0"} ${l.uom}`, tone: p?.short ? ("warn" as const) : undefined }; });
    return (
      <div className="screen" onClick={unlockAudio}>
        <Strip left={<b>{t("release.title")}</b>} middle={chosen.reference} online={navigator.onLine} queued={0} stuck={0} />
        <div className="body">
          <Prompt step={`${chosen.reference} · ${chosen.customer?.name ?? ""}`} title={t("release.confirmTitle")} sub={t("release.confirmSub", { at: hhmm(chosen.pickedAt), by: (chosen.pickedBy ?? "").replace(/^user:/, ""), pallets: picking?.pallets.length ?? 0 })} />
          <Rows rows={rows} />
          <p className="hint">{t("release.hint")}</p>
        </div>
        <Actions>
          <Btn kind="quiet" icon="back" grow={false} onClick={() => setChosen(null)} />
          <Btn kind="primary" disabled={busy} onClick={() => void release([chosen.id])}>{t("release.do")}</Btn>
        </Actions>
      </div>
    );
  }
  return (
    <div className="screen" onClick={() => { unlockAudio(); focusScan(inputRef.current); }}>
      <Strip left={<b>{t("release.title")}</b>} middle={orders ? t("release.waiting", { n: orders.length }) : ""} online={navigator.onLine} queued={0} stuck={0} />
      <div className="body">
        {banner && <Banner {...banner} />}
        <Prompt step={t("release.title")} title={t("release.listTitle")} sub={t("release.listSub")} />
        <ScanBar inputRef={inputRef} placeholder={t("release.scan")} />
        <div className="list">
          {orders?.length === 0 && <p className="hint">{t("release.none")}</p>}
          {orders?.map((o) => <ListItem key={o.id} title={`${o.reference} · ${o.customer?.name ?? ""}`} desc={t("release.row", { at: hhmm(o.pickedAt), by: (o.pickedBy ?? "").replace(/^user:/, ""), pallets: (details.get(o.id)?.picking as Picking | null)?.pallets.length ?? "–", carrier: o.carrier ? `${o.carrier} ${hhmm(o.pickupAt)}`.trim() : t("release.pickup") })} onClick={() => open(o)} />)}
        </div>
      </div>
      <Actions>
        <Btn kind="quiet" icon="back" grow={false} onClick={onClose} />
        {byCarrier.map(([carrier, xs]) => <Btn key={carrier} kind="secondary" disabled={busy} onClick={() => { if (window.confirm(t("release.allConfirm", { n: xs.length, carrier }))) void release(xs.map((x) => x.id)); }}>{t("release.all", { carrier })}</Btn>)}
      </Actions>
    </div>
  );
}
